import React, { useState, useEffect, createContext, useContext } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import ChatInterface from './components/ChatInterface';
import SubscriptionRequired from './components/SubscriptionRequired';
import { ottiTheme } from './theme/ottiTheme';
import './App.css';

interface CopilotSubscription {
  is_active: boolean;
  plan?: string;
  expires_at?: string;
}

interface User {
  username: string;
  email?: string;
  copilot_subscription?: CopilotSubscription;
  [key: string]: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const response = await fetch('/api/auth/user', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        if (data.authenticated && data.user) {
          setUser(data.user);

          // Check if user has active copilot subscription
          const subscription = data.user.copilot_subscription;
          const isActive = subscription && subscription.is_active === true;
          setHasSubscription(isActive);

          setLoading(false);
          return;
        }
      }

      // Not authenticated - redirect to OAuth login
      window.location.href = '/oauth/login';
    } catch (error) {
      console.error('Error checking auth status:', error);
      // On error, also redirect to login
      window.location.href = '/oauth/login';
    }
  };

  const logout = () => {
    // Navigate directly to logout endpoint - this will handle the full logout flow
    // including redirecting to OAuth provider logout and then back to our app
    window.location.href = '/oauth/logout';
  };

  const authValue: AuthContextType = {
    user,
    loading,
    logout
  };

  return (
    <AuthContext.Provider value={authValue}>
      <ThemeProvider theme={ottiTheme}>
        <CssBaseline />
        <Box 
          sx={{ 
            height: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            backgroundColor: '#F3F3F6', // Light gray background
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100vh',
                gap: 2
              }}
            >
              <CircularProgress />
              <Typography variant="body1">Redirecting to login...</Typography>
            </Box>
          ) : !hasSubscription ? (
            <SubscriptionRequired />
          ) : (
            <ChatInterface />
          )}
        </Box>
      </ThemeProvider>
    </AuthContext.Provider>
  );
}

export default App;
